// src/hooks/useMediCureOnData.js
import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { useAuth } from './useAuth';
import { apiConfig } from '../config/apiConfig';
import medipointsApiService from '../services/medipointsApiService';

// Create a context for sharing data across components
const MediCureOnDataContext = createContext({});

// Provider component that wraps your app
export const MediCureOnDataProvider = ({ children }) => {
  const { user, userInfo } = useAuth();
  
  // Shared state for all components
  const [profilePicture, setProfilePicture] = useState(null);
  const [userSubscription, setUserSubscription] = useState({ type: null, activePlans: [] });
  const [activePlans, setActivePlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // 🌟 ENTERPRISE MEDIPOINTS STATE MANAGEMENT
  const [mediPointsBalance, setMediPointsBalance] = useState({
    currentBalance: 0,
    totalEarned: 0,
    totalSpent: 0,
    totalRedeemed: 0,
    tier: 'Bronze',
    pointsToNextTier: 500,
    lastUpdated: null,
    transactionCount: 0,
    
    // 🎯 ENTERPRISE ANALYTICS
    sourceSummary: {
      dailyActivities: 0,
      challenges: 0,
      bonuses: 0,
      completedChallenges: 0,
      streakDays: 0
    },
    achievements: {
      totalBadges: 0,
      totalMilestones: 0,
      latestAchievement: null
    },
    engagement: {
      consistencyScore: 0,
      diversityScore: 0,
      platformEngagementScore: 0,
      disengagementRisk: 'low'
    },
    redemptionSummary: {
      totalRedeemed: 0,
      redemptionCount: 0,
      lastRedemption: null,
      recommendedRedemptionTiming: 'save_more'
    },
    region: 'US'
  });
  const [pointsHistory, setPointsHistory] = useState([]);
  const [isMediPointsLoading, setIsMediPointsLoading] = useState(false);
  const [mediPointsError, setMediPointsError] = useState(null);
  const [isBackendConnected, setIsBackendConnected] = useState(false);
  
  // Cache timestamps to prevent unnecessary refetches
  const [lastFetchTime, setLastFetchTime] = useState({
    profile: null,
    subscription: null,
    profilePicture: null,
    mediPoints: null
  });

  // Cache duration (5 minutes for non-critical data)
  const CACHE_DURATION = 5 * 60 * 1000;
  
  // Helper to check if cache is valid
  const isCacheValid = useCallback((lastFetch) => {
    return lastFetch && (Date.now() - lastFetch < CACHE_DURATION);
  }, [CACHE_DURATION]);

  // Get user's display name (utility function)
  const getUserDisplayName = useCallback(() => {
    if (userInfo?.displayName) return userInfo.displayName;
    if (userInfo?.name) return userInfo.name;
    if (user?.name) return user.name;
    if (userInfo?.firstName) return userInfo.firstName;
    const email = userInfo?.email || user?.username || user?.idTokenClaims?.email;
    if (email) return email.split('@')[0];
    return 'User';
  }, [user, userInfo]);

  // Get greeting based on time
  const getGreeting = useCallback(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    if (hour < 21) return "Good Evening";
    return "Good Night";
  }, []);

  // Fetch profile picture with caching
  const fetchProfilePicture = useCallback(async (forceRefresh = false) => {
    console.log('🖼️ fetchProfilePicture called, user available:', !!user?.localAccountId || !!user?.username);
    
    if (!user?.localAccountId && !user?.username) {
      console.log('🖼️ No user ID available, skipping profile picture fetch');
      return;
    }
    
    // Check cache validity
    if (!forceRefresh && isCacheValid(lastFetchTime.profilePicture) && profilePicture) {
      console.log('🖼️ Using cached profile picture:', profilePicture);
      return;
    }

    try {
      const userId = user.localAccountId || user.username;
      // 🌍 PROFILE PICTURE: EU Regional Routing Fix
      const country = userInfo?.country || 
                     userInfo?.personalDetails?.country || 
                     user?.idTokenClaims?.country || 
                     user?.country ||
                     'United States';
      
      console.log('🖼️ Fetching profile picture for user:', userId);
      console.log('🖼️ Using country:', country);
      console.log('🖼️ API URL:', `${apiConfig.backendUrl}/api/profiles/${userId}/picture?code=${apiConfig.functionKeys.profilePicture}`);
      
      const response = await fetch(
        `${apiConfig.backendUrl}/api/profiles/${userId}/picture?code=${apiConfig.functionKeys.profilePicture}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Country': country  // 🌍 Critical for EU regional routing
          }
        }
      );

      console.log('🖼️ Profile picture API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🖼️ Profile picture API response data:', data);
        
        if (data.currentPicture) {
          console.log('🖼️ Setting profile picture URL:', data.currentPicture);
          setProfilePicture(data.currentPicture);
          setLastFetchTime(prev => ({ ...prev, profilePicture: Date.now() }));
          console.log('🖼️ Profile picture cached successfully');
        } else {
          console.log('🖼️ No currentPicture in API response');
        }
      } else {
        console.log('🖼️ Profile picture API failed with status:', response.status);
      }
    } catch (error) {
      console.error('🖼️ Error loading profile picture:', error);
    }
  }, [user, userInfo, profilePicture, lastFetchTime.profilePicture, isCacheValid]);

  // Fetch subscription data with caching
  const fetchUserSubscription = useCallback(async (forceRefresh = false) => {
    if (!user?.localAccountId && !user?.username) {
      setIsLoading(false);
      return;
    }

    // Check cache validity
    if (!forceRefresh && isCacheValid(lastFetchTime.subscription) && userSubscription.type !== null) {
      console.log('Using cached subscription data');
      setIsLoading(false);
      return;
    }

    try {
      if (forceRefresh) {
        setIsRefreshing(true);
      } else if (!lastFetchTime.subscription) {
        setIsLoading(true);
      }

      const userId = user.localAccountId || user.username;
      const userCountry = userInfo?.country || 'United States';
      
      console.log('Fetching subscription data...');
      const response = await fetch(
        `${apiConfig.backendUrl}${apiConfig.endpoints.subscription.check(userId)}?code=${apiConfig.functionKeys.checkSubscriptionStatus}`,
        {
          headers: {
            'Authorization': `Bearer ${user.idToken}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data.hasSubscription) {
          setUserSubscription({
            type: data.subscription.type,
            status: data.subscription.status,
            currentPeriodEnd: data.subscription.currentPeriodEnd,
            productName: data.subscription.productName
          });
          setActivePlans(data.activePlans || []);
        } else {
          setUserSubscription({ type: null, activePlans: [] });
          setActivePlans([]);
        }
        setLastFetchTime(prev => ({ ...prev, subscription: Date.now() }));
        setError(null);
        console.log('Subscription data cached');
      } else {
        const errorText = await response.text();
        setError(`Failed to fetch subscription: ${response.status}`);
        console.error('Failed to fetch subscription:', response.status, errorText);
      }
    } catch (error) {
      setError('Network error while fetching subscription');
      console.error('Error fetching subscription:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [user, userInfo, userSubscription.type, lastFetchTime.subscription, isCacheValid]);

  // =====================================================================
  // 🌟 ENTERPRISE MEDIPOINTS MANAGEMENT SYSTEM
  // =====================================================================
  
  // Fetch MediPoints balance and data with caching
  const fetchMediPoints = useCallback(async (forceRefresh = false) => {
    if (!user?.localAccountId && !user?.username) return;
    
    // Check cache validity for MediPoints
    if (!forceRefresh && isCacheValid(lastFetchTime.mediPoints) && mediPointsBalance.lastUpdated) {
      console.log('🎯 Using cached MediPoints data');
      return;
    }

    setIsMediPointsLoading(true);
    setMediPointsError(null);
    
    try {
      const userId = user.localAccountId || user.username;
      // 🌍 ENTERPRISE MEDIPOINTS V3.0: Critical EU Regional Routing Fix
      // Use same comprehensive country detection as ChallengesTab for EU users
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      
      // 🌟 ENTERPRISE: Get subscription tier for enhanced tier calculation
      const subscriptionTier = userSubscription?.type || null;
      
      console.log('🎯 Fetching MediPoints data from enterprise backend...');
      console.log('🌍 MediPoints Regional Detection:', {
        detectedCountry: userCountry,
        subscriptionTier: subscriptionTier,
        source: userInfo?.country ? 'userInfo.country' : 
               userInfo?.personalDetails?.country ? 'userInfo.personalDetails.country' :
               user?.idTokenClaims?.country ? 'user.idTokenClaims.country' :
               user?.country ? 'user.country' : 'fallback',
        region: userCountry === 'United States' ? 'US' : 
               ['Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'United Kingdom', 'Norway', 'Switzerland', 'Iceland', 'Liechtenstein'].includes(userCountry) ? 'EU' : 'US'
      });
      
      // Get comprehensive MediPoints data
      const balanceResult = await medipointsApiService.getBalance(userId, userCountry, authToken, subscriptionTier);
      const historyResult = await medipointsApiService.getHistory(userId, 50, userCountry, authToken, subscriptionTier);
      
      if (balanceResult.success) {
        setMediPointsBalance(balanceResult.balance);
        setIsBackendConnected(true);
        setLastFetchTime(prev => ({ ...prev, mediPoints: Date.now() }));
        console.log('💰 MediPoints balance loaded:', balanceResult.balance);
      }
      
      if (historyResult.success) {
        setPointsHistory(historyResult.history);
        console.log('📜 MediPoints history loaded:', historyResult.history.length, 'transactions');
      }
      
    } catch (error) {
      console.error('❌ Error fetching MediPoints data:', error);
      setMediPointsError(error.message);
      setIsBackendConnected(false);
    } finally {
      setIsMediPointsLoading(false);
    }
  }, [user, userInfo, mediPointsBalance.lastUpdated, lastFetchTime.mediPoints, isCacheValid, userSubscription?.type]);

  // Add MediPoints with comprehensive tracking
  const addMediPoints = useCallback(async (amount, source, description = '', metadata = {}) => {
    if (!user?.localAccountId && !user?.username) {
      console.warn('❌ Invalid user ID for MediPoints add');
      return { success: false, error: 'No valid user ID' };
    }
    
    try {
      const userId = user.localAccountId || user.username;
      // 🌍 ENTERPRISE MEDIPOINTS V3.0: EU Regional Routing Fix for addMediPoints
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      
      console.log(`➕ Adding ${amount} MediPoints for ${source}`);
      
      const result = await medipointsApiService.addPoints(
        userId, 
        amount, 
        source, 
        description, 
        metadata, 
        userCountry, 
        authToken
      );
      
      if (result.success) {
        // Update local state immediately for responsive UI
        setMediPointsBalance(prev => ({
          ...prev,
          currentBalance: result.newBalance || (prev.currentBalance + amount),
          totalEarned: (prev.totalEarned + amount),
          lastUpdated: new Date().toISOString()
        }));
        
        // Add transaction to local history for immediate UI feedback
        const newTransaction = {
          id: `temp_${Date.now()}`,
          amount: amount,
          source: source,
          description: description,
          timestamp: new Date().toISOString(),
          type: 'earned',
          ...metadata
        };
        
        setPointsHistory(prev => [newTransaction, ...prev.slice(0, 49)]);
        setIsBackendConnected(true);
        setLastFetchTime(prev => ({ ...prev, mediPoints: Date.now() }));
        
        console.log('✅ MediPoints added successfully:', result);
        return { success: true, data: result };
      } else {
        console.error('❌ Failed to add MediPoints:', result.error);
        return { success: false, error: result.error };
      }
      
    } catch (error) {
      console.error('❌ Error adding MediPoints:', error);
      setIsBackendConnected(false);
      return { success: false, error: error.message };
    }
  }, [user, userInfo]);

  // Deduct MediPoints with comprehensive tracking
  const deductMediPoints = useCallback(async (amount, reason, description = '') => {
    if (!user?.localAccountId && !user?.username) {
      console.warn('❌ Invalid user ID for MediPoints deduction');
      return { success: false, error: 'No valid user ID' };
    }
    
    try {
      const userId = user.localAccountId || user.username;
      // 🌍 ENTERPRISE MEDIPOINTS V3.0: EU Regional Routing Fix for deductMediPoints
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      
      console.log(`➖ Deducting ${amount} MediPoints for ${reason}`);
      
      const result = await medipointsApiService.deductPoints(
        userId, 
        amount, 
        reason, 
        description, 
        userCountry, 
        authToken
      );
      
      if (result.success) {
        // Update local state immediately for responsive UI
        setMediPointsBalance(prev => ({
          ...prev,
          currentBalance: result.newBalance || (prev.currentBalance - amount),
          totalSpent: (prev.totalSpent + amount),
          lastUpdated: new Date().toISOString()
        }));
        
        // Add transaction to local history for immediate UI feedback
        const newTransaction = {
          id: `temp_${Date.now()}`,
          amount: -amount,
          source: reason,
          description: description,
          timestamp: new Date().toISOString(),
          type: 'spent'
        };
        
        setPointsHistory(prev => [newTransaction, ...prev.slice(0, 49)]);
        setIsBackendConnected(true);
        setLastFetchTime(prev => ({ ...prev, mediPoints: Date.now() }));
        
        console.log('✅ MediPoints deducted successfully:', result);
        return { success: true, data: result };
      } else {
        console.error('❌ Failed to deduct MediPoints:', result.error);
        return { success: false, error: result.error };
      }
      
    } catch (error) {
      console.error('❌ Error deducting MediPoints:', error);
      setIsBackendConnected(false);
      return { success: false, error: error.message };
    }
  }, [user, userInfo]);

  // Get comprehensive MediPoints statistics
  const getMediPointsStats = useCallback(async () => {
    if (!user?.localAccountId && !user?.username) return null;
    
    try {
      const userId = user.localAccountId || user.username;
      // 🌍 ENTERPRISE MEDIPOINTS V3.0: EU Regional Routing Fix for getMediPointsStats
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      
      const result = await medipointsApiService.getStats(userId, userCountry, authToken);
      
      if (result.success) {
        console.log('📊 MediPoints statistics loaded:', result.stats);
        return result.stats;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error fetching MediPoints statistics:', error);
      return null;
    }
  }, [user, userInfo]);

  // Save comprehensive MediPoints data (bulk operation)
  const saveMediPointsData = useCallback(async (mediPointsData) => {
    if (!user?.localAccountId && !user?.username) return { success: false, error: 'No valid user ID' };
    
    try {
      const userId = user.localAccountId || user.username;
      // 🌍 ENTERPRISE MEDIPOINTS V3.0: EU Regional Routing Fix for saveMediPointsData
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      
      console.log('💾 Saving comprehensive MediPoints data...');
      
      const result = await medipointsApiService.saveMediPointsData(
        userId, 
        mediPointsData, 
        userCountry, 
        authToken
      );
      
      if (result.success) {
        console.log('✅ MediPoints data saved successfully');
        setIsBackendConnected(true);
        setLastFetchTime(prev => ({ ...prev, mediPoints: Date.now() }));
        return { success: true, data: result.data };
      } else {
        console.error('❌ Failed to save MediPoints data:', result.error);
        return { success: false, error: result.error };
      }
      
    } catch (error) {
      console.error('❌ Error saving MediPoints data:', error);
      setIsBackendConnected(false);
      return { success: false, error: error.message };
    }
  }, [user, userInfo]);

  // Test MediPoints backend connection
  const testMediPointsConnection = useCallback(async () => {
    if (!user?.localAccountId && !user?.username) return false;
    
    try {
      const userId = user.localAccountId || user.username;
      // 🌍 ENTERPRISE MEDIPOINTS V3.0: EU Regional Routing Fix for testMediPointsConnection
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      
      const result = await medipointsApiService.testConnection(userId, userCountry);
      
      if (result.success) {
        setIsBackendConnected(true);
        console.log('✅ MediPoints backend connection successful');
        return true;
      }
      
      setIsBackendConnected(false);
      return false;
    } catch (error) {
      console.error('❌ MediPoints backend connection failed:', error);
      setIsBackendConnected(false);
      return false;
    }
  }, [user, userInfo]);

  // Initial data load - fetch in parallel for speed (including MediPoints)
  useEffect(() => {
    console.log('🔍 useMediCureOnData useEffect triggered');
    console.log('🔍 user:', !!user, user?.localAccountId || user?.username);
    console.log('🔍 userInfo:', !!userInfo, userInfo?.email);
    
    if (user) {
      console.log('✅ User available - fetching profile picture regardless of userInfo');
      // Fetch profile picture if we have user (don't wait for userInfo)
      fetchProfilePicture();
      
      if (userInfo) {
        console.log('✅ Both user and userInfo available - fetching subscription and MediPoints');
        Promise.all([
          fetchUserSubscription(),
          fetchMediPoints()
        ]);
      } else {
        console.log('⚠️ userInfo not available yet - only fetching profile picture');
      }
    } else {
      console.log('❌ No user available - not fetching any data');
    }
  }, [user, userInfo, fetchProfilePicture, fetchUserSubscription, fetchMediPoints]);

  // Manual refresh all data (including MediPoints)
  const refreshAll = useCallback(async () => {
    console.log('Manual refresh triggered - including MediPoints');
    setIsRefreshing(true);
    await Promise.all([
      fetchProfilePicture(true),
      fetchUserSubscription(true),
      fetchMediPoints(true)
    ]);
  }, [fetchProfilePicture, fetchUserSubscription, fetchMediPoints]);

  // Refresh specific data
  const refreshProfile = useCallback(() => fetchProfilePicture(true), [fetchProfilePicture]);
  const refreshSubscription = useCallback(() => fetchUserSubscription(true), [fetchUserSubscription]);
  const refreshMediPoints = useCallback(() => fetchMediPoints(true), [fetchMediPoints]);

  // Update functions for when data changes (e.g., after profile update)
  const updateProfilePicture = useCallback((newPicture) => {
    setProfilePicture(newPicture);
    setLastFetchTime(prev => ({ ...prev, profilePicture: Date.now() }));
  }, []);

  const updateSubscription = useCallback((newSubscription) => {
    setUserSubscription(newSubscription);
    setLastFetchTime(prev => ({ ...prev, subscription: Date.now() }));
  }, []);

  const updateActivePlans = useCallback((newPlans) => {
    setActivePlans(newPlans);
    // Also update the cache timestamp since plans are part of subscription data
    setLastFetchTime(prev => ({ ...prev, subscription: Date.now() }));
  }, []);

  // Clear all cache (useful for logout or testing) - including MediPoints
  const clearCache = useCallback(() => {
    console.log('Clearing all cache including MediPoints');
    setLastFetchTime({ profile: null, subscription: null, profilePicture: null, mediPoints: null });
    setProfilePicture(null);
    setUserSubscription({ type: null, activePlans: [] });
    setActivePlans([]);
    // Clear MediPoints data
    setMediPointsBalance({
      currentBalance: 0,
      totalEarned: 0,
      totalSpent: 0,
      tier: 'Bronze',
      pointsToNextTier: 500,
      lastUpdated: null
    });
    setPointsHistory([]);
    setIsBackendConnected(false);
    setMediPointsError(null);
  }, []);

  // Force refresh if window regains focus after being hidden for a while
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const now = Date.now();
        const profileAge = now - (lastFetchTime.profilePicture || 0);
        const subscriptionAge = now - (lastFetchTime.subscription || 0);
        
        // If data is older than cache duration, refresh it
        if (profileAge > CACHE_DURATION || subscriptionAge > CACHE_DURATION) {
          console.log('Page visible again, data is stale, refreshing...');
          refreshAll();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastFetchTime, refreshAll, CACHE_DURATION]);

  // 🌟 ENTERPRISE SPECIALIZED FUNCTIONS FOR PRODUCTION INTEGRATION
  
  // Add MediPoints for daily activity completion
  const addMediPointsForDailyActivity = useCallback(async (amount, activityType, dataType, metadata = {}) => {
    if (!user?.localAccountId && !user?.username) {
      console.warn('❌ Invalid user ID for daily activity points');
      return { success: false, error: 'No valid user ID' };
    }
    
    try {
      const userId = user.localAccountId || user.username;
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      const subscriptionTier = userSubscription?.type || null;
      
      console.log(`🎯 Adding ${amount} points for daily activity: ${activityType}`);
      
      const result = await medipointsApiService.addPointsForDailyActivity(
        userId, 
        amount, 
        activityType, 
        dataType, 
        {
          consecutiveDay: metadata.consecutiveDay,
          streakDays: metadata.streakDays,
          deviceType: metadata.deviceType,
          dataValidated: metadata.dataValidated,
          ...metadata
        }, 
        userCountry, 
        authToken,
        subscriptionTier
      );
      
      if (result.success) {
        // Update backend connection status
        setIsBackendConnected(true);
        
        // 🔄 CRITICAL FIX: Re-fetch balance from backend to ensure consistency
        console.log('🔄 Refreshing balance from backend after points addition...');
        
        try {
          // Force refresh the balance from backend
          await fetchMediPoints(true);
          console.log('✅ Balance refreshed successfully from backend');
        } catch (refreshError) {
          console.warn('⚠️ Balance refresh failed, using returned data:', refreshError);
          
          // Fallback: Update local state with returned data
          setMediPointsBalance(prev => ({
            ...prev,
            currentBalance: result.data?.newBalance || (prev.currentBalance + amount),
            totalEarned: (prev.totalEarned + amount),
            sourceSummary: {
              ...prev.sourceSummary,
              dailyActivities: prev.sourceSummary.dailyActivities + amount,
              streakDays: metadata.streakDays || prev.sourceSummary.streakDays
            },
            engagement: {
              ...prev.engagement,
              consistencyScore: metadata.streakDays ? Math.min(100, (metadata.streakDays / 30) * 100) : prev.engagement.consistencyScore
            },
            lastUpdated: new Date().toISOString()
          }));
        }
        
        // Add enhanced transaction to local history
        const newTransaction = {
          id: result.data?.transaction?.id || `temp_${Date.now()}`,
          amount: amount,
          source: 'Daily Health Activity',
          description: `Completed ${activityType}`,
          timestamp: new Date().toISOString(),
          type: 'earned',
          metadata: {
            activityType,
            dataType,
            ...metadata
          }
        };
        
        setPointsHistory(prev => [newTransaction, ...prev.slice(0, 49)]);
        console.log('✅ Daily activity points added successfully:', result);
        return { success: true, data: result.data };
      } else {
        console.error('❌ Failed to add daily activity points:', result.error);
        setIsBackendConnected(false);
        return { success: false, error: result.error };
      }
      
    } catch (error) {
      console.error('❌ Error adding daily activity points:', error);
      setIsBackendConnected(false);
      return { success: false, error: error.message };
    }
  }, [user, userInfo, userSubscription?.type, fetchMediPoints]);

  // Add MediPoints for challenge completion
  const addMediPointsForChallengeCompletion = useCallback(async (amount, challengeId, challengeType, metadata = {}) => {
    if (!user?.localAccountId && !user?.username) {
      console.warn('❌ Invalid user ID for challenge completion points');
      return { success: false, error: 'No valid user ID' };
    }
    
    try {
      const userId = user.localAccountId || user.username;
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      
      console.log(`🏆 Adding ${amount} points for challenge completion: ${challengeId}`);
      
      const result = await medipointsApiService.addPointsForChallengeCompletion(
        userId, 
        amount, 
        challengeId, 
        challengeType, 
        {
          completionDate: new Date().toISOString(),
          challengeDuration: metadata.challengeDuration,
          progressData: metadata.progressData,
          achievementUnlocked: metadata.achievementUnlocked,
          ...metadata
        }, 
        userCountry, 
        authToken
      );
      
      if (result.success) {
        // Update backend connection status
        setIsBackendConnected(true);
        
        // Update local state with enhanced analytics
        setMediPointsBalance(prev => ({
          ...prev,
          currentBalance: result.data?.newBalance || (prev.currentBalance + amount),
          totalEarned: (prev.totalEarned + amount),
          sourceSummary: {
            ...prev.sourceSummary,
            challenges: prev.sourceSummary.challenges + amount,
            completedChallenges: prev.sourceSummary.completedChallenges + 1
          },
          achievements: {
            ...prev.achievements,
            totalBadges: metadata.achievementUnlocked ? prev.achievements.totalBadges + 1 : prev.achievements.totalBadges
          },
          lastUpdated: new Date().toISOString()
        }));
        
        // Add enhanced transaction to local history
        const newTransaction = {
          id: result.data?.transaction?.id || `temp_${Date.now()}`,
          amount: amount,
          source: 'Challenge Completion',
          description: `Completed challenge: ${challengeId}`,
          timestamp: new Date().toISOString(),
          type: 'earned',
          metadata: {
            challengeId,
            challengeType,
            ...metadata
          }
        };
        
        setPointsHistory(prev => [newTransaction, ...prev.slice(0, 49)]);
        console.log('✅ Challenge completion points added successfully:', result);
        return { success: true, data: result.data };
      } else {
        console.error('❌ Failed to add challenge completion points:', result.error);
        return { success: false, error: result.error };
      }
      
    } catch (error) {
      console.error('❌ Error adding challenge completion points:', error);
      return { success: false, error: error.message };
    }
  }, [user, userInfo]);

  // Get analytics dashboard for production insights
  const getMediPointsAnalytics = useCallback(async () => {
    if (!user?.localAccountId && !user?.username) return null;
    
    try {
      const userId = user.localAccountId || user.username;
      const userCountry = userInfo?.country || 
                         userInfo?.personalDetails?.country || 
                         user?.idTokenClaims?.country || 
                         user?.country ||
                         'United States';
      const authToken = user?.idToken;
      
      console.log('📊 Fetching MediPoints analytics dashboard...');
      
      const result = await medipointsApiService.getAnalyticsDashboard(userId, userCountry, authToken);
      
      if (result.success) {
        console.log('📊 Analytics dashboard loaded:', result.analytics);
        return result.analytics;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error fetching analytics dashboard:', error);
      return null;
    }
  }, [user, userInfo]);

  // 📊 GET REAL MEDIPOINTS - Returns current balance for ranking/display
  const getUserRealMediPoints = useCallback(() => {
    // Return current balance from the mediPointsBalance state
    console.log('🎯 getUserRealMediPoints called, returning:', mediPointsBalance?.currentBalance || 0);
    return mediPointsBalance?.currentBalance || 0;
  }, [mediPointsBalance]);

  const value = {
    // Data
    profilePicture,
    userSubscription,
    activePlans,
    isLoading,
    isRefreshing,
    error,
    
    // 🌟 ENTERPRISE MEDIPOINTS DATA
    mediPointsBalance,
    pointsHistory,
    isMediPointsLoading,
    mediPointsError,
    isBackendConnected,
    
    // Utility functions
    getUserDisplayName,
    getGreeting,
    getUserRealMediPoints,
    
    // Actions
    refreshAll,
    refreshProfile,
    refreshSubscription,
    refreshMediPoints,
    updateProfilePicture,
    updateSubscription,
    updateActivePlans,
    clearCache,
    
    // 🌟 ENTERPRISE MEDIPOINTS ACTIONS
    addMediPoints,
    deductMediPoints,
    fetchMediPoints,
    getMediPointsStats,
    saveMediPointsData,
    testMediPointsConnection,
    
    // 🌟 SPECIALIZED PRODUCTION METHODS
    addMediPointsForDailyActivity,
    addMediPointsForChallengeCompletion,
    getMediPointsAnalytics,
    
    // Cache control
    isCacheValid,
    CACHE_DURATION
  };

  return (
    <MediCureOnDataContext.Provider value={value}>
      {children}
    </MediCureOnDataContext.Provider>
  );
};

// Custom hook to use the shared data
export const useMediCureOnData = () => {
  const context = useContext(MediCureOnDataContext);
  if (!context) {
    throw new Error('useMediCureOnData must be used within MediCureOnDataProvider');
  }
  return context;
};

// Export a utility to force real-time fetch for critical data
export const fetchRealTimeData = async (endpoint, options = {}) => {
  // This bypasses cache for critical health monitoring
  console.log('Fetching real-time data:', endpoint);
  return fetch(endpoint, {
    ...options,
    headers: {
      ...options.headers,
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
};